// @flow

import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import classNames from "classnames";
import { route } from "@routes/helper";
import type { State } from "@store/reducers";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { formatDate } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import type {
  ReferralProcessesData,
  ReferralProcess,
} from "@modules/service/reducer/referrals/types";
import type { FilterState } from "@modules/service/types";
import { Button, ButtonGroup, GSGrid, Icons, View, Loader } from "@components";
import { ReferralsPromotion, ReferralAddEditModal } from "@modules/service/components";

type OwnProps = $Exact<{
  clientId: number,
  isEnabled: boolean,
}>;

type PropsFromState = $Exact<{
  referralData: ReferralProcessesData,
  referralSchema: ?Object,
  isLoading: boolean,
  schema: ?Object,
  container: ?ReferralProcess,
}>;

type PropsFromDispatch = $Exact<{
  push: typeof push,
  newReferral: typeof actionCreators.NewReferral,
  getReferralList: typeof actionCreators.GetReferralList,
  getSchema: typeof actionCreators.GetReferralSchema,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type StateProps = $Exact<{
  referralsFilter: FilterState,
}>;

export function ReferralsTable(props: Props) {
  const { clientId } = props;
  const [referralsFilter, setReferralsFilter] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    props.isEnabled && props.getReferralList({ clientId, isActive: referralsFilter });
  }, [referralsFilter, clientId]);

  useEffect(() => {
    if (props.getSchema) {
      props.getSchema({ clientId });
    }
    props.getHierarchyTree({ clientId });
  }, []);

  const toggleModal = () => {
    if (!isModalOpen) {
      props.newReferral();
    }
    setIsModalOpen(prev => !prev);
  };

  const referralsList = props.referralData.content;

  const referralsHasAlertConfigurations =
    props?.referralSchema?.employeeLedReferrals?.properties?.alertConfigurations?.items?.properties
      ?.id?.oneOf?.length || false;

  const referralsColumns = [
    {
      name: props.t("ID"),
      key: "id",
      width: 60,
    },
    {
      name: props.t("REFERRAL_PROCESS_NAME"),
      renderCell: ({ row }: { row: ReferralProcess }) => GSGrid.render.ellipsis(row.content.name),
      align: "left",
    },
    {
      name: props.t("DATE_CREATED"),
      width: 100,
      renderCell: ({ row }: { row: ReferralProcess }) => formatDate(row.createdAt),
    },
    {
      name: props.t("CREATED_BY"),
      width: "min-content",
      renderCell: ({ row }: { row: ReferralProcess }) => formatAuthoredBy(row.createdBy),
      align: "left",
    },

    {
      key: "employeeReferrals",
      name: props.t("EMPLOYEE_REFERRAL"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_NAVIGATOR_TABLE_EMPLOYEE_REFERRAL"),
      },
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const employeeReferrals = getValuesAndScore(row).employeeReferrals;
        return getColorPillRender(employeeReferrals.value, employeeReferrals.isScoring);
      },
    },
    {
      key: "managerReferrals",
      name: props.t("MANAGER_REFERRAL"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_NAVIGATOR_TABLE_MANAGER_REFERRALS"),
      },
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const managerReferrals = getValuesAndScore(row).managerReferrals;
        return getColorPillRender(managerReferrals.value, managerReferrals.isScoring);
      },
    },
    {
      key: "managerKpi",
      name: props.t("MANAGER_KPI"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_NAVIGATOR_TABLE_MANAGER_KPI"),
      },
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const managerKpi = getValuesAndScore(row).managerKpi;
        return getColorPillRender(managerKpi.value, managerKpi.isScoring);
      },
    },
    {
      key: "supplierTracking",
      name: props.t("SUPPLIER_TRACKING"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_NAVIGATOR_TABLE_SUPPLIER_TRACKING"),
      },
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const supplierTracking = getValuesAndScore(row).supplierTracking;
        return getColorPillRender(supplierTracking.value, supplierTracking.isScoring);
      },
    },
    {
      key: "suplierKpi",
      name: props.t("SUPLIER_KPI"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_NAVIGATOR_TABLE_SUPLIER_KPI"),
      },
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const suplierKpi = getValuesAndScore(row).suplierKpi;
        return getColorPillRender(suplierKpi.value, suplierKpi.isScoring);
      },
    },
    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: ReferralProcess }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },
    {
      name: props.t("STATUS"),
      width: 105,
      renderCell: ({ row }: { row: ReferralProcess }) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("VIEW"),
      width: 50,
      onCellClick: (row: ReferralProcess) => openReferralDetails(row.id),
      renderCell: ({ row }: { row: ReferralProcess }) => GSGrid.render.materialIcon("search"),
    },
  ];
  type ValueAndScore = {
    value: any,
    isScoring: boolean,
  };

  type ValuesAndScoreRecord = {
    employeeReferrals: ValueAndScore,
    managerReferrals: ValueAndScore,
    managerKpi: ValueAndScore,
    supplierTracking: ValueAndScore,
    suplierKpi: ValueAndScore,
  };
  function getValuesAndScore(row: ReferralProcess): ValuesAndScoreRecord {
    const employeeReferrals = !!row.content.employeeReferrals;
    const managerReferrals = !!row.content.managerReferrals;
    const managerKpi = row.content.managerReferralsSettings?.kpis[0]?.kpiMeasure || 0;
    const supplierTracking = !!row.content.supplierTracking.isActive;
    const suplierKpi = row.content.supplierTracking?.kpis[0]?.kpiMeasure || 0;

    return {
      employeeReferrals: {
        value: props.t(employeeReferrals ? "YES" : "NO"),
        isScoring: employeeReferrals,
      },
      managerReferrals: {
        value: props.t(managerReferrals ? "YES" : "NO"),
        isScoring: managerReferrals,
      },
      managerKpi: {
        value: managerKpi || props.t("N/A"),
        isScoring: managerKpi > 0 && managerKpi <= 72,
      },
      supplierTracking: {
        value: props.t(supplierTracking ? "YES" : "NO"),
        isScoring: supplierTracking,
      },
      suplierKpi: {
        value: suplierKpi || props.t("N/A"),
        isScoring: suplierKpi > 0 && suplierKpi <= 48,
      },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  function openReferralDetails(id: number) {
    props.push(
      route("ADMINISTRATION_SERVICE_REFERRAL_DETAILS", {
        clientId,
        processId: id,
      }),
    );
  }

  function referralsHandleChangeFilter(referralsFilter: FilterState) {
    return function() {
      setReferralsFilter(referralsFilter);
    };
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "1rem", 0]}>
        {props.t("WELLBEING_REFERRALS_INFORMATION")}
      </View>
      <View className="gs-container">
        {props.isEnabled && (
          <View margin={[0, 0, "1rem", 0]} direction="row" justifyContent="space-between">
            <ButtonGroup
              disabled={!referralsHasAlertConfigurations}
              config={[
                {
                  label: props.t("ALL"),
                  color: referralsFilter === null && "selected",
                  onClick: referralsHandleChangeFilter(null),
                },
                {
                  label: props.t("ACTIVE"),
                  color: referralsFilter === true && "selected",
                  onClick: referralsHandleChangeFilter(true),
                },
                {
                  label: props.t("INACTIVE"),
                  color: referralsFilter === false && "selected",
                  onClick: referralsHandleChangeFilter(false),
                },
              ]}
            ></ButtonGroup>
            <Button
              materialIcon="add_box"
              onClick={toggleModal}
              disabled={!referralsHasAlertConfigurations}
            >
              {props.t("ADD")}
            </Button>
          </View>
        )}

        {props.isEnabled && !referralsHasAlertConfigurations && (
          <View alignItems="center">
            <div className="alert alert-danger">
              {props.t("EMPLOYEE_REFERRAL_NO_ALERT_CONFIGURATION")}
            </div>
          </View>
        )}

        {(props.isEnabled && <GSGrid columns={referralsColumns} rows={referralsList} />) || (
          <ReferralsPromotion />
        )}
      </View>
      {isModalOpen && (
        <ReferralAddEditModal
          clientId={clientId}
          editing={false}
          container={props.container}
          schema={props.schema}
          onClose={toggleModal}
          currentFilter={referralsFilter}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  referralData: serviceSelectors.referralProcessData(state),
  referralSchema: serviceSelectors.getReferralSchema(state),
  isLoading: serviceSelectors.isLoading(state),
  schema: serviceSelectors.getReferralSchema(state),
  container: serviceSelectors.getCurrentReferral(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  push,
  newReferral: actionCreators.NewReferral,
  getReferralList: actionCreators.GetReferralList,
  getSchema: actionCreators.GetReferralSchema,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ReferralsTable);
